import useSWR from 'swr'

import type {Iri, UrlId} from '../../../@types/Api'
import type {SpotCollection, SpotItem, SpotPartial} from '../../../@types/entity/Spot'
import type {FetchOptions} from '../../../@types/fetch'
import {type SpotCollectionFilterGroups, spotCollectionFilterMapping, type SpotCollectionSort} from '../../../@types/filter/Spot'
import {useStableParamValue} from '../../components/StableRouter'
import {useCurrentSpotIri} from '../../states/currentSpot'
import {createCollectionIri, createItemIri} from '../../utils/iri'
import {
  type FetchOption,
  filterToQueryString,
  type StrictUnion,
  useDelete,
  useGet,
  usePatch,
  usePost,
  useUpsert,
} from '../useApi'
import {useEvent} from '../useEvent'
import {useFetcher} from '../useFetcher'

const resource = createCollectionIri('spots')

type GetResponse = {
  readonly spot?: SpotItem
  readonly error?: Error
}

type CollectionResponse = {
  readonly spots?: SpotCollection
  readonly error?: Error
}

type SpotCollectionFilter = StrictUnion<SpotCollectionFilterGroups>
export type {SpotCollectionSort}
export const useSpotGet = (spotIri?: Iri): GetResponse => {
  const {data, error} = useGet(spotIri)

  return {
    spot: data,
    error,
  }
}

export const useSpotCollection = (filter: SpotCollectionFilter | undefined, fetchOptions?: FetchOption<SpotCollectionSort>, enabled = true): CollectionResponse => {
  const querystring = filterToQueryString(spotCollectionFilterMapping, enabled ? filter : undefined, fetchOptions)
  querystring?.set('pagination', 'false')
  const {data, error} = useSWR(enabled && querystring ? `${createCollectionIri('spots')}?${querystring.toString()}` : null)
  return {
    spots: data,
    error,
  }
}

export const useSpotIriFromLocation = (): Iri | undefined => {
  const spotId = useStableParamValue('spotId') as UrlId | undefined

  return createItemIri('spots', spotId)
}
export const useSpotHelperGetCurrent = (): GetResponse => {
  return useSpotGet(useCurrentSpotIri())
}

/** `itemsPerPage` is required: without a fetch option `filterToQueryString` asks for the whole table. */
export function useSpotsFetcher(): (filter: SpotCollectionFilter, init?: FetchOptions) => Promise<SpotCollection> {
  const {fetcher} = useFetcher()

  return useEvent((filter: SpotCollectionFilter, init: FetchOptions = {}): Promise<SpotCollection> => {
    const querystring = filterToQueryString(spotCollectionFilterMapping, filter, {itemsPerPage: 10})

    return fetcher(querystring ? `${resource}?${querystring.toString()}` : resource, init)
  })
}

type UpdatePayload = Partial<CreatePayload>

type CreatePayload = {
  name: string

  routeScale?: string
  boulderScale?: string
  visible?: boolean
  anonymousComment?: boolean
}

export function useSpotCreate(): (payload: CreatePayload) => Promise<SpotItem> {
  return usePost<SpotItem>('spots')
}

export function useSpotUpdate(): (spot: SpotPartial, payload: UpdatePayload) => Promise<SpotItem> {
  return usePatch<SpotItem>()
}

export function useSpotUpsert(): (
  spot: SpotPartial | undefined,
  payload: UpdatePayload,
  createExtraPayload?: Omit<CreatePayload, keyof UpdatePayload>,
) => Promise<SpotItem> {
  return useUpsert(useSpotCreate(), useSpotUpdate())
}

export function useSpotDelete(): (spot: SpotPartial) => Promise<void> {
  return useDelete()
}
